import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Collapse, Paper, TextField, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import FilterField, { DateFilterField, PresetFilterField, DatePresetType } from "../Trading/Filters/FilterField";
import { OptionType } from "../../Types/CostObject.type";
import { getPresetRange } from "../../Helpers/datePreset.helper";
import { FIELD_ROW_GRID_SX } from "./CostObjectform";

const STATUSES: OptionType[] = [
    { value: "PENDING", label: "Pending" },
    { value: "COMMITTED", label: "Committed" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
];
const REQSET: OptionType[] = [
    { value: "True", label: "Yes" },
    { value: "False", label: "No" },
];
const INCPNL: OptionType[] = [
    { value: "True", label: "Yes" },
    { value: "False", label: "No" },
];

// ─── Multi-select filter value shape ──────────────────────────────────────────
interface MultiFilterValue {
    values: string[];
    isAll: boolean;
}

const emptyMultiValue = (): MultiFilterValue => ({ values: [], isAll: false });

const MULTI_FIELDS = [
    "status",
    "costType",
    "book",
    "trader",
    "strategy",
    "clearer",
    "broker",
    "product",
    "counterparty",
    "pnlCategory",
    "requiresSettlement",
    "includeInPnl",
] as const;

type MultiFieldKey = (typeof MULTI_FIELDS)[number];

const ROW1_MULTI_FIELDS: MultiFieldKey[] = [ "costType", ];
const ROW2_MULTI_FIELDS: MultiFieldKey[] = [
    "book",
    "pnlCategory",
    // "trader",
    "strategy",
    "requiresSettlement",
    "includeInPnl",
    // "clearer",
    // "broker",
    // "product",
    // "counterparty",
];
const ROW3_MULTI_FIELDS: MultiFieldKey[] = [  "status"];

interface CostFilterValues {
    status: MultiFilterValue;
    costType: MultiFilterValue;
    book: MultiFilterValue;
    trader: MultiFilterValue;
    strategy: MultiFilterValue;
    clearer: MultiFilterValue;
    broker: MultiFilterValue;
    product: MultiFilterValue;
    counterparty: MultiFilterValue;
    pnlCategory: MultiFilterValue;
    requiresSettlement: MultiFilterValue;
    includeInPnl: MultiFilterValue;
    description: string;
    effectiveDateStart: string;
    effectiveDateEnd: string;
    dueDateStart: string;
    dueDateEnd: string;
    effectiveDatePreset: DatePresetType | null;   
    dueDatePreset: DatePresetType | null;
    costId: string;
    amountFrom: string;
    amountTo: string;
}

const emptyFilters: CostFilterValues = {
    status: emptyMultiValue(),
    costType: emptyMultiValue(),
    book: emptyMultiValue(),
    trader: emptyMultiValue(),
    strategy: emptyMultiValue(),
    clearer: emptyMultiValue(),
    broker: emptyMultiValue(),
    product: emptyMultiValue(),
    counterparty: emptyMultiValue(),
    pnlCategory: emptyMultiValue(),
    requiresSettlement: emptyMultiValue(),
    includeInPnl: emptyMultiValue(),
    description: "",
    effectiveDatePreset: null,
    effectiveDateStart: "",
    effectiveDateEnd: "",
    dueDatePreset: null,
    dueDateStart: "",
    dueDateEnd: "",
    costId: "",
    amountFrom: "",
    amountTo: "",
};

const FILTER_PARAM_MAP: Record<string, string> = {
    status: "fieldName_status",
    costType: "fieldName_cost_type_name",
    book: "fieldName_book",
    trader: "fieldName_trader",
    strategy: "fieldName_strategy",
    clearer: "fieldName_clearer",
    broker: "fieldName_broker",
    product: "fieldName_product",
    counterparty: "fieldName_counterparty",
    pnlCategory: "fieldName_PnL_Category",
    requiresSettlement: "fieldName_requires_settlement",
    includeInPnl: "fieldName_exclude_from_pnl",
    description: "fieldName_description",
    effectiveDateStart: "fieldName_effective_date",
    effectiveDateEnd: "fieldName_effective_date",
    dueDateStart: "fieldName_due_date",
    dueDateEnd: "fieldName_due_date",
    costId: "fieldName_cost_booking_id",
    amountFrom: "fieldName_amount",
    amountTo: "fieldName_amount",
};

const FILTER_LABELS: Record<string, string> = {
    status: "Status",
    costType: "Cost Type",
    book: "Book",
    trader: "Trader",
    strategy: "Strategy",
    clearer: "Clearer",
    broker: "Broker",
    product: "Product",
    counterparty: "Counterparty",
    pnlCategory: "P&L Category / Apply To",
    requiresSettlement: "Requires Settlement",
    includeInPnl: "Include in PNL",
    description: "Description",
    effectiveDateStart: "Effective Date From",
    effectiveDateEnd: "Effective Date",
    dueDateStart: "Due Date From",
    dueDateEnd: "Due Date",
    tradeDatePreset: "Trade Date",
    costId: "Cost ID",
    amountFrom: "Amount From",
    amountTo: "Amount To",
};

// ─── Local preset helpers ─────────────────────────────────────────────────────
const DATE_PRESET_CUSTOM: DatePresetType = "Custom";


interface CostObjectFilterComponentProps {
    onApplyFilters: (filters: Record<string, any>) => void;
}

const CostObjectFilterComponent: React.FC<CostObjectFilterComponentProps> = ({
    onApplyFilters,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [filters, setFilters] = useState<CostFilterValues>(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState<CostFilterValues>(emptyFilters);

    const [costTypeOptions, setCostTypeOptions] = useState<OptionType[]>([]);
    const [bookOptions, setBookOptions] = useState<OptionType[]>([]);
    const [traderOptions, setTraderOptions] = useState<OptionType[]>([]);
    const [strategyOptions, setStrategyOptions] = useState<OptionType[]>([]);
    const [clearerOptions, setClearerOptions] = useState<OptionType[]>([]);
    const [brokerOptions, setBrokerOptions] = useState<OptionType[]>([]);
    const [productOptions, setProductOptions] = useState<OptionType[]>([]);
    const [counterpartyOptions, setCounterpartyOptions] = useState<OptionType[]>([]);
    const [pnlCategoryOptions, setPnlCategoryOptions] = useState<OptionType[]>([]);

    useEffect(() => {
        const load = (
            url: string,
            setter: React.Dispatch<React.SetStateAction<OptionType[]>>,
            mapFn: (item: any) => OptionType
        ) => {
            axiosInstance
                .get(`${backendbaseUrl}${url}`)
                .then((res) => {
                    const results = res.data?.results ?? res.data ?? [];
                    setter(results.map(mapFn).filter((o: OptionType) => o.label));
                })
                .catch(() => { });
        };

        load("/paper-app/Cost-Type-Dropdown/", setCostTypeOptions, (item) => ({
            value: item.cost_name ?? "",
            label: item.cost_name ?? "",
        }));
        load("/paper-app/Cost-Book-Dropdown/", setBookOptions, (item) => ({
            value: item.name ?? item.book ?? "",
            label: item.name ?? item.book ?? item.label,
        }));
        load("/paper-app/Cost-Trader-Dropdown/", setTraderOptions, (item) => ({
            value: item.email ?? "",
            label: item.email ?? "",
        }));
        load("/paper-app/Cost-Strategy-Dropdown/", setStrategyOptions, (item) => ({
            value: item.name ?? item.strategy ?? "",
            label: item.name ?? item.strategy ?? item.label,
        }));
        load("/paper-app/Cost-Clearer-Dropdown/", setClearerOptions, (item) => ({
            value: item.name ?? item.clearer ?? "",
            label: item.name ?? item.clearer ?? item.label,
        }));
        load("/paper-app/Cost-Broker-Dropdown/", setBrokerOptions, (item) => ({
            value: item.name ?? item.broker ?? "",
            label: item.name ?? item.broker ?? item.label,
        }));
        load("/paper-app/Cost-Contract-Dropdown/", setProductOptions, (item) => ({
            value: item.contract_name ?? "",
            label: item.contract_name ?? "",
        }));
        load("/paper-app/Cost-CounterParty-Dropdown/", setCounterpartyOptions, (item) => ({
            value: item.customer_company_name ?? "",
            label: item.customer_company_name ?? "",
        }));
        axiosInstance
            .get(`${backendbaseUrl}/paper-app/PnL-Category-Dropdown/`)
            .then((res) => {
                const results = res.data?.data ?? res.data?.results ?? res.data ?? [];
                setPnlCategoryOptions(
                    results
                        .map((item: any) => ({
                            value: item.value ?? item.id ?? "",
                            label: item.label ?? item.name ?? item.category_name ?? "",
                        }))
                        .filter((opt: OptionType) => !!opt.label)
                );
            })
            .catch(() => { });
    }, []);

    const optionsByField: Record<MultiFieldKey, OptionType[]> = {
        status: STATUSES,
        costType: costTypeOptions,
        book: bookOptions,
        trader: traderOptions,
        strategy: strategyOptions,
        clearer: clearerOptions,
        broker: brokerOptions,
        product: productOptions,
        counterparty: counterpartyOptions,
        pnlCategory: pnlCategoryOptions,
        requiresSettlement: REQSET,
        includeInPnl: INCPNL,
    };

    const isFieldEmpty = (key: keyof CostFilterValues, values: CostFilterValues) => {
        const v = values[key];
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return !v;
        if (typeof v === "object" && "values" in (v as any)) {
            return !(v as MultiFilterValue).values.length;
        }
        return false;
    };

    const hasActiveFilters = useMemo(
        () =>
            (Object.keys(appliedFilters) as (keyof CostFilterValues)[]).some(
                (key) => !isFieldEmpty(key, appliedFilters)
            ),
        [appliedFilters]
    );

    const activeChips = useMemo(() => {
        const chips: { key: string; label: string; value: string }[] = [];

        if (appliedFilters.effectiveDateStart || appliedFilters.effectiveDateEnd) {
            const start = appliedFilters.effectiveDateStart;
            const end = appliedFilters.effectiveDateEnd;
            const display = start && end && start !== end ? `${start} to ${end}` : (end || start);
            chips.push({ key: "effectiveDate", label: "Effective Date", value: display });
        }

        if (appliedFilters.dueDateStart || appliedFilters.dueDateEnd) {
            const start = appliedFilters.dueDateStart;
            const end = appliedFilters.dueDateEnd;
            const display = start && end && start !== end ? `${start} to ${end}` : (end || start);
            chips.push({ key: "dueDate", label: "Due Date", value: display });
        }

        if (appliedFilters.amountFrom || appliedFilters.amountTo) {
            const display = `${appliedFilters.amountFrom || "…"} – ${appliedFilters.amountTo || "…"}`;
            chips.push({ key: "amountRange", label: "Amount", value: display });
        }
        (Object.keys(appliedFilters) as (keyof CostFilterValues)[]).forEach((key) => {
            if (
                key === "effectiveDatePreset" ||
                key === "effectiveDateStart" ||
                key === "effectiveDateEnd" ||
                key === "dueDatePreset" ||
                key === "dueDateStart" ||
                key === "dueDateEnd" ||
                key === "amountFrom" ||
                key === "amountTo"
            ) {
                return;
            }
            if (isFieldEmpty(key, appliedFilters)) return;

            const raw = appliedFilters[key];
            let display: string;

            if (typeof raw === "string") {
                display = raw;
            } else {
                const values = (raw as MultiFilterValue).values;
                const options = MULTI_FIELDS.includes(key as MultiFieldKey)
                    ? optionsByField[key as MultiFieldKey]
                    : [];
                display = values
                    .map((v) => options.find((o) => String(o.value) === String(v))?.label ?? v)
                    .join(", ");
            }

            chips.push({ key, label: FILTER_LABELS[key] ?? key, value: display });
        });

        return chips;
    }, [
        appliedFilters,
        costTypeOptions,
        bookOptions,
        traderOptions,
        strategyOptions,
        clearerOptions,
        brokerOptions,
        productOptions,
        counterpartyOptions,
        pnlCategoryOptions
    ]);

    const handleSelectFilterChange = (
        field: string,
        values: string[],
        isAllClicked = false
    ) => {
        setFilters((prev) => ({
            ...prev,
            [field]: { values, isAll: isAllClicked && values.length > 0 },
        }));
    };

    const handleDescriptionChange = (value: string) => {
        setFilters((prev) => ({ ...prev, description: value }));
    };

    const handleCostIdChange = (value: string) => {
        setFilters((prev) => ({ ...prev, costId: value }));
    };

    const handleAmountFromChange = (value: string) => {
        if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
            setFilters((prev) => ({ ...prev, amountFrom: value }));
        }
    };

    const handleAmountToChange = (value: string) => {
        if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
            setFilters((prev) => ({ ...prev, amountTo: value }));
        }
    };

    const handleDateFieldChange =
        (field: "effectiveDateStart" | "effectiveDateEnd" | "dueDateStart" | "dueDateEnd") =>
            (value: string) => {
                setFilters((prev) => ({ ...prev, [field]: value }));
            };

    const buildAppliedParams = (values: CostFilterValues) => {
        const params: Record<string, any> = {};

        // Multi-select fields
        MULTI_FIELDS.forEach((key) => {
            const v = values[key];
            if (v.values.length > 0) {
                params[FILTER_PARAM_MAP[key]] = v.values;
            }
        });

        if (values.description) {
            params[FILTER_PARAM_MAP.description] = `${values.description}_contains`;
        }

        if (values.costId) {
            params[FILTER_PARAM_MAP.costId] = `${values.costId}_contains`;
        }

        if (values.amountFrom || values.amountTo) {
            const from = (values.amountFrom || "").trim();
            const to = (values.amountTo || "").trim();

            if (from && to) {
                params[FILTER_PARAM_MAP.amountFrom] = `${from}_${to}`;
            } else if (from) {
                params[FILTER_PARAM_MAP.amountFrom] = from;
            } else if (to) {
                params[FILTER_PARAM_MAP.amountFrom] = to;
            }
        }

        if (values.effectiveDateStart || values.effectiveDateEnd) {
            const start = values.effectiveDateStart || values.effectiveDateEnd;
            const end = values.effectiveDateEnd || values.effectiveDateStart;
            if (start === end) {
                params[FILTER_PARAM_MAP.effectiveDateEnd] = start; 
            } else {
                params["__clientRange_effective_date"] = [start, end];
            }
        }

        if (values.dueDateStart || values.dueDateEnd) {
            const start = values.dueDateStart || values.dueDateEnd;
            const end = values.dueDateEnd || values.dueDateStart;
            if (start === end) {
                params[FILTER_PARAM_MAP.dueDateEnd] = start;
            } else {
                params["__clientRange_due_date"] = [start, end];
            }
        } return params;
    };
    const handleApply = () => {
        setAppliedFilters(filters);
        onApplyFilters(buildAppliedParams(filters));
    };

    const handleClearAll = () => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        onApplyFilters({});
    };

    const handleRemoveChip = (key: string) => {
        let updated = { ...appliedFilters };

        if (key === "effectiveDate") {
            updated = {
                ...updated,
                effectiveDatePreset: null,
                effectiveDateStart: "",
                effectiveDateEnd: "",
            };
        } else if (key === "dueDate") {
            updated = {
                ...updated,
                dueDatePreset: null,
                dueDateStart: "",
                dueDateEnd: "",
            };
        } else if (key === "amountRange") {
            updated = {
                ...updated,
                amountFrom: "",
                amountTo: "",
            };
        } else {
            const clearedValue: string | MultiFilterValue =
                typeof appliedFilters[key as keyof CostFilterValues] === "string"
                    ? ""
                    : emptyMultiValue();
            updated = { ...updated, [key]: clearedValue };
        }

        setFilters(updated);
        setAppliedFilters(updated);
        onApplyFilters(buildAppliedParams(updated));
    };

    const isFormEmpty = (Object.keys(filters) as (keyof CostFilterValues)[]).every((key) =>
        isFieldEmpty(key, filters)
    );


    const filterTextFieldSx = {
        width: "100%",
        "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            minHeight: "32px",
            height: "32px",
            padding: "1px 6px",
        },
        "& .MuiOutlinedInput-root": {
            paddingTop: "2px",
            paddingBottom: "2px",
        },
        "& .MuiInputLabel-root": {
            fontSize: "0.8rem",
            transform: "translate(12px, 7px) scale(1)",
        },
        "& .MuiInputLabel-shrink": {
            transform: "translate(12px, -9px) scale(0.75)",
        },
        "& input": {
            fontSize: "0.7rem",
            padding: "1px 2px",
        },
    };

    return (
        <Paper elevation={1} sx={{ mb: 1, overflow: "hidden", border: "1px solid #e0e0e0" }}>
            <Box
                sx={{
                    px: 1.25,
                    py: 0.65,
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#eeeeee" },
                }}
                onClick={() => setIsExpanded((prev) => !prev)}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FilterListIcon fontSize="small" sx={{ color: hasActiveFilters ? "#000" : "#9e9e9e" }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>
                        {hasActiveFilters ? "Filters (Active)" : "Filters"}
                    </Typography>
                </Box>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>

            {hasActiveFilters && (
                <Box sx={{ px: 1, py: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                    {activeChips.map((chip) => (
                        <Chip
                            key={chip.key}
                            label={
                                <span>
                                    {chip.label}: <strong>{chip.value}</strong>
                                </span>
                            }
                            onDelete={() => handleRemoveChip(chip.key)}
                            size="small"
                            sx={{
                                backgroundColor: "#e3f2fd",
                                color: "#585bcc",
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: "20px",
                            }}
                        />
                    ))}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        size="small"
                        onClick={handleClearAll}
                        sx={{ textTransform: "none", fontSize: "0.7rem", fontWeight: 600, color: "#585bcc" }}
                    >
                        Clear All
                    </Button>
                </Box>
            )}

            <Collapse in={isExpanded}>
                <Box sx={{ p: 1.5 }}>
                    <Box sx={FIELD_ROW_GRID_SX}>
                        <PresetFilterField
                            label="Effective Date"
                            value={filters.effectiveDatePreset}
                            onChange={(preset) => {
                                if (!preset || preset === DATE_PRESET_CUSTOM) {
                                    setFilters((p) => ({
                                        ...p,
                                        effectiveDatePreset: preset,
                                        effectiveDateStart: "",
                                        effectiveDateEnd: "",
                                    }));
                                    return;
                                }
                                const { start, end } = getPresetRange(preset);
                                setFilters((p) => ({
                                    ...p,
                                    effectiveDatePreset: preset,
                                    effectiveDateStart: start,
                                    effectiveDateEnd: end,
                                }));
                            }}
                        />

                        {filters.effectiveDatePreset === DATE_PRESET_CUSTOM && (
                            <>
                                <DateFilterField
                                    label="Effective Date From"
                                    value={filters.effectiveDateStart}
                                    onChange={(val) =>
                                        setFilters((p) => ({
                                            ...p,
                                            effectiveDateStart: val,
                                            effectiveDateEnd: p.effectiveDateEnd || val,
                                        }))
                                    }
                                />
                                <DateFilterField
                                    label="Effective Date To"
                                    value={filters.effectiveDateEnd}
                                    onChange={(val) => setFilters((p) => ({ ...p, effectiveDateEnd: val }))}
                                />
                            </>
                        )}
                        {/* <PresetFilterField
                            label="Due Date"
                            value={filters.dueDatePreset}
                            onChange={(preset) => {
                                if (!preset || preset === DATE_PRESET_CUSTOM) {
                                    setFilters((p) => ({
                                        ...p,
                                        dueDatePreset: preset,
                                        dueDateStart: "",
                                        dueDateEnd: "",
                                    }));
                                    return;
                                }
                                const { start, end } = getPresetRange(preset);
                                setFilters((p) => ({
                                    ...p,
                                    dueDatePreset: preset,
                                    dueDateStart: start,
                                    dueDateEnd: end,
                                }));
                            }}
                        />

                        {filters.dueDatePreset === DATE_PRESET_CUSTOM && (
                            <>
                                <DateFilterField
                                    label="Due Date From"
                                    value={filters.dueDateStart}
                                    onChange={(val) =>
                                        setFilters((p) => ({
                                            ...p,
                                            dueDateStart: val,
                                            dueDateEnd: p.dueDateEnd || val,
                                        }))
                                    }
                                />
                                <DateFilterField
                                    label="Due Date To"
                                    value={filters.dueDateEnd}
                                    onChange={(val) => setFilters((p) => ({ ...p, dueDateEnd: val }))}
                                />
                            </>
                        )} */}

                        <TextField
                            label="Cost ID"
                            placeholder="Search Cost ID..."
                            size="small"
                            value={filters.costId}
                            onChange={(e) => handleCostIdChange(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={filterTextFieldSx}
                        />

                        <TextField
                            label="Amount From"
                            placeholder="Min"
                            size="small"
                            type="number"
                            value={filters.amountFrom}
                            onChange={(e) => handleAmountFromChange(e.target.value)}
                            inputProps={{ step: "any" }}
                            InputLabelProps={{ shrink: true }}
                            sx={filterTextFieldSx}
                        />

                        <TextField
                            label="Amount To"
                            placeholder="Max"
                            size="small"
                            type="number"
                            value={filters.amountTo}
                            onChange={(e) => handleAmountToChange(e.target.value)}
                            inputProps={{ step: "any" }}
                            InputLabelProps={{ shrink: true }}
                            sx={filterTextFieldSx}
                        />

                        {ROW1_MULTI_FIELDS.map((field) => (
                            <FilterField
                                key={field}
                                label={FILTER_LABELS[field]}
                                field={field}
                                options={optionsByField[field]}
                                value={filters[field]}
                                single
                                onChange={handleSelectFilterChange}
                            />
                        ))}
                    </Box>

                    <Box sx={FIELD_ROW_GRID_SX}>
                        {ROW2_MULTI_FIELDS.map((field) => (
                            <FilterField
                                key={field}
                                label={FILTER_LABELS[field]}
                                field={field}
                                options={optionsByField[field]}
                                value={filters[field]}
                                single
                                onChange={handleSelectFilterChange}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "flex-end",
                        }}
                    >
                        {ROW3_MULTI_FIELDS.map((field) => (
                            <Box key={field} sx={{ flex: "1 1 180px", minWidth: 150 }}>
                                <FilterField
                                    label={FILTER_LABELS[field]}
                                    field={field}
                                    options={optionsByField[field]}
                                    value={filters[field]}
                                    single
                                    onChange={handleSelectFilterChange}
                                />
                            </Box>
                        ))}

                        <Box sx={{ flex: "1 1 260px", minWidth: 200 }}>
                            <TextField
                                label="Description"
                                placeholder="Contains text..."
                                size="small"
                                fullWidth
                                value={filters.description}
                                onChange={(e) => handleDescriptionChange(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={filterTextFieldSx}
                            />
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", flex: "0 0 auto", flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleApply}
                                sx={{
                                    height: "32px",
                                    minWidth: "100px",
                                    backgroundColor: "#00bcd4",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#008c9e" },
                                }}
                            >
                                Apply
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleClearAll}
                                disabled={isFormEmpty && !hasActiveFilters}
                                sx={{
                                    height: "32px",
                                    minWidth: "100px",
                                    backgroundColor: "#f44336",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#ba000d" },
                                    "&:disabled": { backgroundColor: "#ccc" },
                                }}
                            >
                                Clear All
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default CostObjectFilterComponent;